<template>
	<v-select class="SalesChannelSelectorField CommonSelectField" v-model="currentModel" :items="dropdownItems" item-value="id"
		return-object multiple hideDetails="true" variant="outlined" color="#00aeef" :placeholder="placeholder" @update:menu="onMenuUpdate">
		<template #selection="{ index }">
			<template v-if="index === 0">
				<template v-if="compactChips">
					<v-chip
						v-if="chips && chips.length"
						:key="chips[0].id"
						size="small"
						:closable="isClosable(chips[0])"
						@click:close="removeChip(chips[0])"
						@mousedown.stop.prevent
					>
						{{ chips[0].title }}
					</v-chip>
					<span v-if="chips && chips.length > 1" class="compact-chip-count" @mousedown.stop.prevent>
						+{{ chips.length - 1 }}
					</span>
				</template>
				<template v-else-if="chips">
					<v-chip
						v-for="chip in displayedChips"
						:key="chip.id"
						size="small"
						:closable="isClosable(chip)"
						@click:close="removeChip(chip)"
						@mousedown.stop.prevent
					>
						{{ chip.title }}
					</v-chip>
					<span v-if="hiddenChipCount > 0" class="compact-chip-count" @mousedown.stop.prevent>
						+{{ hiddenChipCount }}
					</span>
				</template>
			</template>
		</template>
		<template #item="{ item }">
			<v-list-item class="CommonSelectFieldItem" @click="onItemClick(item.raw)" @keydown.enter.space="onItemClick(item.raw)">
				<template v-slot:prepend="{ }">
					<v-list-item-action start>
						<v-checkbox-btn :key="`${item.id}-${forceUp}`" :model-value="itemState(item.raw) === true" :indeterminate="isIndeterminate(item.raw)" :disabled="item.raw.disabled" color="#00aeef" @click.stop="setItemState(item.raw)"></v-checkbox-btn>
					</v-list-item-action>
				</template>
				<template #title>
					<span class="item-title">{{ item.raw.title[selectedLocale] ?? item.raw.title?.de ?? item.raw.title }}</span>
					<span v-if="item.raw.available" class="item-subtitle">{{ `${item.raw.available} ${$t('text.available').toLowerCase()}` }}</span>
				</template>
				<template v-if="item.raw.available" v-slot:append>
					<v-icon color="#000">mdi-chevron-right</v-icon>
				</template>
			</v-list-item>
		</template>
		<template #prepend-item>
			<v-list-item v-if="selectedClientItem || selectedChannelItem" class="CommonSelectFieldItem" @click="selectedChannelItem ? selectedChannelItem = null : selectedClientItem = null" @keydown.enter.space="selectedChannelItem ? selectedChannelItem = null : selectedClientItem = null">
				<template v-slot:prepend>
					<div class="back-container">
						<v-icon color="#000">mdi-chevron-left</v-icon>
						<span>{{ $t('text.back') }}</span>
					</div>
				</template>
				<template #title>
					<span class="item-title bold">{{ (selectedChannelItem ?? selectedClientItem).title[selectedLocale] ?? (selectedChannelItem ?? selectedClientItem).title.de }}</span>
				</template>
			</v-list-item>
		</template>
	</v-select>
</template>

<script>
import Common from '@/mixins/Common.vue'

export default {
	name: 'SalesChannelSelector',
	mixins: [ Common ],
	props: {
		modelValue: Array,
		propClientAssignments: Array,
		serviceProvider: Object,
		dataCy: String,
		app: String,
		clientAssignmentLocked: Boolean,
		isWaiting: Boolean,
		locked: Boolean,
		clients: Array,
		placeholder: String,
		compactChips: Boolean,
		collapseChipsWhenClosed: {
			type: Boolean,
			default: false
		},
		collapsedChipLimit: {
			type: Number,
			default: 10
		}
	},
	data: () => ({
		model: [],
		loading: false,
		selectedClientItem: null,
		selectedChannelItem: null,
		allowUncheckingHomebase: false,
		forceUp: 0,
		initModel: null,
		isMenuOpen: false,
	}),
	computed: {
		selectedClient() {
			return this.$store.state.selectedClient
		},
		spTrustLevel() {
			return this.serviceProvider?.fields?.trustLevel?.de
		},
		enableSelection() {
			return this.spTrustLevel === 3 || this.spEnabledSetting(this.selectedClient.fields.marketplaceSettings?.de, 'spSalesChannelSelectionEnabled')
		},
		isCurrentClientEditing() {
			return this.selectedClientItem?.id === this.selectedClient.sys.id
		},
		dropdownItems() {
			if (this.selectedChannelItem) {
				const cli = this.clients.find(x => x.sys.id === this.selectedClientItem.id)
				const salesChannel = cli.fields.salesChannels.de.find(x => x.sys.id === this.selectedChannelItem.id)
				const categories = salesChannel.fields.resellerCategories.de ?? []
				const normalizeId = value => {
					const nr = Number(value)
					return Number.isFinite(nr) ? nr : value
				}
				const items = []
				for (const category of categories) {
					items.push({
						id: normalizeId(category.fields.peakId.de),
						title: category.fields.title,
						disabled: !this.isCurrentClientEditing
					})
					for (const subCategory of category.fields.subCategories.de ?? []) {
						items.push({
							id: normalizeId(subCategory.peakId),
							title: subCategory.title,
							disabled: !this.isCurrentClientEditing
						})
					}
				}
				return items
			}
			if (this.selectedClientItem) {
				const client = this.clients.find(x => x.sys.id === this.selectedClientItem.id)
				return client.fields.salesChannels.de.map(x => ({
					id: x.sys.id, 
					title: x.fields.title, 
					disabled: this.clientAssignmentLocked || x.fields.mandatory.de || !this.enableSelection || !this.isCurrentClientEditing,
					available: x.fields.isResellerChannel.de
						? (x.fields.resellerCategories.de?.length ?? 0) + x.fields.resellerCategories.de.reduce((acc, r) => acc + r.fields.subCategories.de.length, 0)
						: 0 }))
			}
			return this.clients.map(x => ({ 
				id: x.sys.id,
				title: x.fields.title,
				disabled: this.clientAssignmentLocked || (!this.allowUncheckingHomebase && this.isHomeBaseClient({ id: x.sys.id })),
				available: x.fields.salesChannels.de.length
			 }))
		},
		currentModel: {
			get() {
				const sc = this.selectedChannelItem
				const cl = this.selectedClientItem

				if (cl && sc) {
					const m = this.model.find(x => x.id === cl.id)
					const a = m?.selected?.find(x => x.id === sc.id)
					return a?.selected?.length ? a.selected : [{ id: 'dummy' }]
				}

				if (cl) {
					const m = this.model.find(x => x.id === cl.id)
					return m?.selected?.length ? m.selected : [{ id: 'dummy' }]
				}

				return this.model
			},

			set(val) {
				const sc = this.selectedChannelItem
				const cl = this.selectedClientItem

				if (cl && sc) {
					let mi = this.model.findIndex(x => x.id === cl.id)
					if (mi === -1) {
						this.model.push({
							id: cl.id,
							title: cl.title,
							available: cl.available,
							isSelected: true,
							selected: []
						})
						mi = this.model.length - 1
					}

					const clientRef = this.model[mi]
					if (!Array.isArray(clientRef.selected)) {
						clientRef.selected = []
					}

					let si = clientRef.selected.findIndex(x => x.id === sc.id)
					if (si === -1) {
						clientRef.selected.push({
							id: sc.id,
							title: sc.title,
							available: sc.available,
							isSelected: true,
							selected: []
						})
						si = clientRef.selected.length - 1
					}

					const channelRef = clientRef.selected[si]
					if (!Array.isArray(channelRef.selected)) {
						channelRef.selected = []
					}

					channelRef.selected = val
					this.ensureMandatorySalesChannelsForClient(cl.id)
					return
				}

				if (cl) {
					let mi = this.model.findIndex(x => x.id === cl.id)
					if (mi === -1) {
						this.model.push({
							id: cl.id,
							title: cl.title,
							available: cl.available,
							isSelected: true,
							selected: []
						})
						mi = this.model.length - 1
					}
					this.model[mi].selected = Array.isArray(val) ? val : []
					return
				}

				this.model = val
			}
		},
		chips() {
			const chips = []
			for (const client of this.model.filter(x => x.id !== 'dummy')) {
				if (client.id === 'dummy') {
					continue
				}
				if (client.selected?.filter(x => x.id !== 'dummy')?.length) {
					for (const sc of client.selected) {
						if (sc.id === 'dummy') {
							continue
						}
						if (sc.selected?.filter(x => x.id !== 'dummy')?.length) {
							for (const rc of sc.selected) {
								if (rc.id === 'dummy') {
									continue
								}
								const scTitle = sc.title?.[this.selectedLocale] ?? sc.title?.de ?? 'Unknown'
								const clientTitle = client.title?.[this.selectedLocale] ?? client.title?.de ?? 'Unknown'
								const cli = this.resolveClient(client.id)
								const salesChannel = cli.fields.salesChannels.de.find(x => x.sys.id === sc.id)
								const normalizeId = value => {
									const nr = Number(value)
									return Number.isFinite(nr) ? nr : value
								}
								const categories = []
								for (const category of salesChannel.fields.resellerCategories.de ?? []) {
									categories.push({ id: normalizeId(category.fields.peakId.de), title: category.fields.title })
									for (const subCategory of category.fields.subCategories.de ?? []) {
										categories.push({ id: normalizeId(subCategory.peakId), title: subCategory.title })
									}
								}
								const rcId = normalizeId(rc)
								const foundCat = categories.find(x => x.id === rcId)
								if (!foundCat) {
									chips.push({ id: rcId, title: `${scTitle} (${clientTitle})`, clientId: client.id, scId: sc.id })
									continue
								}
								const catTitle = foundCat.title?.[this.selectedLocale] ?? foundCat.title?.de ?? foundCat.title
								chips.push({ id: rcId, title: `${scTitle} | ${catTitle} (${clientTitle})`, clientId: client.id, scId: sc.id })
							}
						} else {
							const scTitle = sc.title?.[this.selectedLocale] ?? sc.title?.de ?? 'Unknown'
							const clientTitle = client.title?.[this.selectedLocale] ?? client.title?.de ?? 'Unknown'
							chips.push({ id: sc.id, title: `${scTitle} (${clientTitle})`, clientId: client.id })
						}
					}
				} else {
					const clientTitle = client.title?.[this.selectedLocale] ?? client.title?.de ?? 'Unknown'
					chips.push({ id: client.id, title: clientTitle })
				}
			}
			return chips
		},
		displayedChips() {
			if (!Array.isArray(this.chips) || !this.chips.length) {
				return []
			}
			if (!this.collapseChipsWhenClosed || this.isMenuOpen || this.compactChips) {
				return this.chips
			}
			const safeLimit = Math.max(1, Number(this.collapsedChipLimit) || 1)
			return this.chips.slice(0, safeLimit)
		},
		hiddenChipCount() {
			return Math.max(0, (this.chips?.length ?? 0) - (this.displayedChips?.length ?? 0))
		}
	},
	watch: {
		modelValue() {
			this.model = JSON.parse(JSON.stringify(this.modelValue ?? []))
		}
	},
	methods: {
		resolveClient(clientId) {
			const direct = this.clients.find(c => c.sys.id === clientId)
			if (direct) return direct
			const assignment = this.serviceProvider?.fields?.clientAssignments?.de?.find(ca => ca?.fields?.client?.de?.sys?.id === clientId)
			return assignment?.fields?.client?.de
		},
		onDropdownClosed() {
			this.$nextTick(() => {
				this.selectedChannelItem = null
				this.selectedClientItem = null
			})
		},
		onMenuUpdate(open) {
			this.isMenuOpen = open === true
			if (!open) {
				this.onDropdownClosed()
			}
		},
		isIndeterminate(item) {
			const modelClient = this.currentModel.find(x => x.id === item.id)
			const selectedCount = modelClient?.selected?.filter(x => x.id !== 'dummy')?.length ?? 0
			const total = item.available ?? 0
			return selectedCount > 0 && selectedCount < total
		},
		itemState(item) {
			const modelClient = this.currentModel.find(x => (x.id ?? x) === (item.id ?? item))
			if (!modelClient || (!Number.isInteger(modelClient) && !modelClient.isSelected)) {
				return false
			}
			return true
		},
		currentClientEditing(cl) {
			return cl?.id === this.selectedClient.sys.id
		},
		isHomeBaseClient(cl) {
			return this.propClientAssignments?.find(c => c.fields.client.de.sys.id === cl.id)?.fields?.isHomebase?.de
		},
		emitModel() {
			this.forceUp++
			this.$emit("update:modelValue", this.model)
		},
		ensureMandatorySalesChannelsForClient(clientId) {
			const clientRef = this.model.find(x => x.id === clientId)
			if (!clientRef) {
				return
			}
			const client = this.clients.find(x => x.sys.id === clientId)
			if (!client?.fields?.salesChannels?.de) {
				return
			}
			if (!Array.isArray(clientRef.selected)) {
				clientRef.selected = []
			}
			const selectedIds = new Set(clientRef.selected.map(sc => sc.id))
			for (const sc of client.fields.salesChannels.de) {
				if (sc.fields?.mandatory?.de && !selectedIds.has(sc.sys.id)) {
					clientRef.selected.push({
						id: sc.sys.id,
						title: sc.fields.title,
						isSelected: true,
						selected: []
					})
				}
			}
		},
		setItemState(item) {
			const key = x => (x && typeof x === 'object' ? x.id : x)
			const id = key(item)
			const normalizeId = value => {
				const nr = Number(value)
				return Number.isFinite(nr) ? nr : value
			}
			const matchId = (a, b) => normalizeId(a) === normalizeId(b)
			const includesId = (list, value) => list.some(x => matchId(x, value))
			const normalizeList = list => list.map(x => normalizeId(x))
			let cur = this.currentModel

			const idx = cur.findIndex(x => key(x) === id)
			const sc = this.selectedChannelItem
			const cl = this.selectedClientItem

			if (idx !== -1) {
				if (cl && sc) {
					const salesChannel = this.clients
						.find(x => x.sys.id === cl.id)
						?.fields?.salesChannels?.de
						?.find(x => x.sys.id === sc.id)
					const category = salesChannel?.fields?.resellerCategories?.de
						?.find(c => matchId(c.fields?.peakId?.de, id))
					if (category) {
						const childIds = normalizeList(category.fields?.subCategories?.de?.map(s => s.peakId) ?? [])
						const next = cur.filter(x => !matchId(x, id) && !childIds.some(childId => matchId(childId, x)))
						this.currentModel = next
						this.emitModel()
						return
					}
					const parentCategory = salesChannel?.fields?.resellerCategories?.de
						?.find(c => (c.fields?.subCategories?.de ?? []).some(s => matchId(s.peakId, id)))
					if (parentCategory) {
						const parentId = normalizeId(parentCategory.fields.peakId.de)
						const childIds = normalizeList(parentCategory.fields?.subCategories?.de?.map(s => s.peakId) ?? [])
						const next = cur.filter(x => !matchId(x, id))
						const remainingChildIds = childIds.filter(childId => includesId(next, childId))
						if (!remainingChildIds.length) {
							const parentIdx = next.findIndex(x => matchId(x, parentId))
							if (parentIdx !== -1) {
								next.splice(parentIdx, 1)
							}
						}
						this.currentModel = next
						this.emitModel()
						return
					}
				}
				if (!sc && !cl && !this.allowUncheckingHomebase && this.isHomeBaseClient(item)) {
					return
				}
				const next = cur.slice()
				next.splice(idx, 1)
				this.currentModel = next
				this.emitModel()
				return
			}
			let modelItem

			if (cl && sc) {
				const salesChannel = this.clients
					.find(x => x.sys.id === cl.id)
					?.fields?.salesChannels?.de
					?.find(x => x.sys.id === sc.id)
				const category = salesChannel?.fields?.resellerCategories?.de
					?.find(c => matchId(c.fields?.peakId?.de, id))
				if (category) {
					const childIds = normalizeList(category.fields?.subCategories?.de?.map(s => s.peakId) ?? [])
					const next = cur.slice()
					if (!includesId(next, id)) {
						next.push(normalizeId(id))
					}
					for (const childId of childIds) {
						if (!includesId(next, childId)) {
							next.push(childId)
						}
					}
					this.currentModel = next
					this.emitModel()
					return
				}
				const parentCategory = salesChannel?.fields?.resellerCategories?.de
					?.find(c => (c.fields?.subCategories?.de ?? []).some(s => matchId(s.peakId, id)))
				if (parentCategory) {
					const parentId = normalizeId(parentCategory.fields.peakId.de)
					const childIds = normalizeList(parentCategory.fields?.subCategories?.de?.map(s => s.peakId) ?? [])
					const next = cur.slice()
					if (!includesId(next, id)) {
						next.push(normalizeId(id))
					}
					const allChildrenSelected = childIds.every(childId => includesId(next, childId))
					if ((allChildrenSelected || parentCategory.fields?.mandatory?.de) && !includesId(next, parentId)) {
						next.push(parentId)
					}
					this.currentModel = next
					this.emitModel()
					return
				}
				modelItem = id
			} else if (cl) {
				const client = this.clients.find(x => x.sys.id === cl.id)
				const scEnt = client?.fields?.salesChannels?.de?.find(x => x.sys.id === item.id)
				let selected = []
				if (scEnt?.fields?.isResellerChannel?.de) {
					const categories = scEnt.fields.resellerCategories?.de ?? []
					if (this.isCurrentClientEditing) {
						const categoryIds = categories.map(c => normalizeId(c.fields.peakId.de))
						const subCategoryIds = categories.flatMap(c => (c.fields.subCategories.de ?? []).map(s => normalizeId(s.peakId)))
						selected = [...categoryIds, ...subCategoryIds]
					} else {
						selected = categories
							.filter(c => c.fields?.mandatory?.de)
							.map(c => normalizeId(c.fields.peakId.de))
					}
				}
				modelItem = {
					id: item.id,
					title: item.title,
					isSelected: true,
					selected
				}
				const mandatory = client?.fields?.salesChannels?.de
					?.filter(scItem => scItem.fields?.mandatory?.de)
					.map(scItem => ({
						id: scItem.sys.id,
						title: scItem.fields.title,
						isSelected: true,
						selected: []
					})) ?? []
				const next = [...cur, modelItem]
				const existing = new Set(next.map(x => x.id))
				for (const m of mandatory) {
					if (!existing.has(m.id)) {
						next.push(m)
					}
				}
				this.currentModel = next
				this.emitModel()
				return
			} else {
				const client = this.clients.find(x => x.sys.id === item.id)
				const canSelectOptional = this.enableSelection && this.currentClientEditing(item) && !this.clientAssignmentLocked
				modelItem = {
					id: item.id,
					title: item.title,
					available: item.available,
					isSelected: true,
					selected: client.fields.salesChannels.de
						.filter(sc => sc.fields.mandatory.de || canSelectOptional)
						.map(sc => {
						let selected = []
						if (sc.fields.isResellerChannel?.de) {
							const categories = sc.fields.resellerCategories?.de ?? []
							if (this.isCurrentClientEditing) {
								const categoryIds = categories.map(c => normalizeId(c.fields.peakId.de))
								const subCategoryIds = categories.flatMap(c => (c.fields.subCategories.de ?? []).map(s => normalizeId(s.peakId)))
								selected = [...categoryIds, ...subCategoryIds]
							} else {
								selected = categories
									.filter(c => c.fields?.mandatory?.de)
									.map(c => normalizeId(c.fields.peakId.de))
							}
						}
						return {
							id: sc.sys.id,
							title: sc.fields.title,
							isSelected: true,
							selected
						}
					})
				}
			}

			this.currentModel = [...cur, modelItem]
			this.emitModel()
		},
		onItemClick(item) {
			if (!item.available) {
				return
			}
			if (!this.selectedClientItem) {
				this.selectedClientItem = item
				return
			}
			if (!this.selectedChannelItem) {
				this.selectedChannelItem = item
				return
			}
		},
		isClosable(chip) {
			if (!chip.clientId && !this.allowUncheckingHomebase && this.isHomeBaseClient(chip)) {
				return false
			}
			if (chip.clientId) {
				const cli = this.clients.find(x => x.sys.id === chip.clientId)
				const salesChannel = cli?.fields?.salesChannels?.de?.find(x => x.sys.id === (chip.scId ?? chip.id))
				if (!salesChannel) {
					return false
				}
				if (chip.scId) {
					return this.currentClientEditing({ id: chip.clientId })
				}
				return !salesChannel.fields.mandatory.de && this.enableSelection && this.currentClientEditing({ id: chip.clientId }) //should this be like that?
			}
			return true
		},
		removeChip(chip) {
			if (!chip.clientId) {
				const index = this.model.findIndex(x => x.id === chip.id)
				this.model.splice(index, 1)
			} else if (!chip.scId) {
				const client = this.model.find(x => x.id === chip.clientId)
				const index = client.selected.findIndex(x => x.id === chip.id)
				client.selected.splice(index, 1)
				if (!client.selected?.length) {
					const cIndex = this.model.findIndex(x => x.id === chip.clientId)
					this.model.splice(cIndex, 1)
				}
			} else {
				const client = this.model.find(x => x.id === chip.clientId)
				const salesChannel = client.selected.find(x => x.id === chip.scId)
				const index = salesChannel.selected.findIndex(x => x === chip.id)
				salesChannel.selected.splice(index, 1)
			}
			this.emitModel()
		},
	},
	created() {
		this.model = JSON.parse(JSON.stringify(this.modelValue ?? []))
		if (this.app === 'ServiceDetail' && (this.userIsHomeOperator || (!this.userIsHomeOperator && !this.userIsOperator))) {
			this.allowUncheckingHomebase = true
		}
	},
}
</script>

<style lang="scss">
@use '@/assets/scss/common-select';
</style>

<style lang="scss" scoped>
.compact-chip-count {
	display: inline-flex;
	align-items: center;
	margin-left: 6px;
	font-size: 13px;
	font-weight: 400;
	line-height: 18px;
	letter-spacing: 0.001px;
	color: black;
}
</style>
